import type { StateInfo } from "@/lib/states";

export interface FaqItem {
  q: string;
  a: string;
}

/**
 * The questions a state page answers, written per level.
 *
 * Until V4 every state got the same answers, so the sixteen states outside the range were
 * told "Yes — Oregon is within or near the broader Lone Star tick range" and that
 * alpha-gal "has been associated with Lone Star tick bites in regions of Oregon" — on a
 * page whose own data said Oregon is west of the range. The answer has to follow the
 * classification, and state-faq.test.ts holds it to that.
 */
export function stateFaq(s: StateInfo): FaqItem[] {
  const name = s.name;
  const peak = s.peakSeason;

  const presence: Record<StateInfo["risk"], string> = {
    low: `Rarely, if at all. ${s.summary} ${s.awareness}`,
    emerging: `In some places. ${name} is at the edge of the Lone Star tick's range, where more are being found. ${s.awareness}`,
    established: `Yes. ${s.summary} ${s.awareness}`,
    high: `Yes. ${name} is in the Lone Star tick's range, and CDC found some of the highest rates of suspected alpha-gal cases here. ${s.awareness}`,
  };

  const season =
    s.risk === "low"
      ? `There is no Lone Star tick season to speak of, because they are rarely reported in ${name}. CDC says ticks in general are most active from April to September, so that is when prevention matters most.`
      : `Lone Star ticks in ${name} are most active ${peak.startsWith("Year-round") ? "all year" : `from ${peak.replace(" – ", " to ")}`}. They can still bite outside those months on warm days.`;

  const alphaGal =
    s.risk === "low"
      ? `In the U.S., alpha-gal syndrome is mostly linked to Lone Star tick bites, and those ticks are rarely found in ${name}. If you get hives, swelling or stomach pain 2 to 6 hours after eating red meat, see a doctor. Mention any tick bites, even ones from a trip.`
      : `It's possible. Alpha-gal syndrome is a delayed allergy to red meat linked to Lone Star tick bites, and those ticks live in ${name}. Most bites do not cause it. If you get hives, swelling or stomach pain 2 to 6 hours after eating red meat, see a doctor.`;

  return [
    { q: `Are there Lone Star ticks in ${name}?`, a: presence[s.risk] },
    { q: `When is tick season in ${name}?`, a: season },
    { q: `Can I get alpha-gal syndrome from a tick bite in ${name}?`, a: alphaGal },
    {
      q: `What should I do after a tick bite in ${name}?`,
      a: "Take the tick out with fine-tipped tweezers, pulling straight up. Wash the bite, keep the tick or a photo of it, and write down the date. Watch for symptoms for the next few weeks. See a doctor if you get a rash, a fever, feel very tired, or react to food.",
    },
    {
      q: `How do I get a tick identified in ${name}?`,
      a: "Seal the tick in a bag with a damp paper towel and take a clear photo. Your state health department or a university extension program may be able to name it or test it.",
    },
  ];
}
